import React, { useEffect, useRef, useState } from 'react';
import BackIcon from '@material-ui/icons/ReplyOutlined';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Edit';
import clsx from 'clsx';

// UI Components
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

import Snackbar from '../../../components/Snackbar';

import Pagination from '../../../components/Pagination';
import SurveyImageCarousel from './imagesCarousel';
import useStyles, { TableHead } from './styles';

// API calls
import { OK, CONFLICT } from '../../../utils/const/http';
import { startSurveyResponseReview, getSurveyResponseById } from '../../../requests/api/survey-response';
import SignatureCanvas from './SignatureCanvas';
import SurveyVideoCarousel from './videosCarousel';
import DetailResponse from '../DetailResponse';

const isValidUrl = (value) => {
  try {
    const validator = new URL(value);
    return validator.protocol === 'http:' || validator.protocol === 'https:';
  } catch {
    return false;
  }
};

const AUDIO_QUESTION_TYPES = ['audio', 'audio-background'];

const isQuestionResponseWithoutAnswer = (questionResponse) => {
  const question = questionResponse.Question || {};
  const type = (question.type || '').toLowerCase();
  const questionChoices = question.QuestionChoices || [];
  const choiceResponses = questionResponse.QuestionChoiceResponses || [];
  const selectedQuestionChoiceId = choiceResponses[0]?.questionChoiceId;
  const selectedQuestionChoice = questionChoices.find(
    (questionChoice) => questionChoice.id === selectedQuestionChoiceId,
  );

  if (type.includes('image')) {
    return !(questionResponse.QuestionResponseImages || [])
      .some((image) => isValidUrl(image.imageUrl));
  }

  if (type === 'video') {
    return !(questionResponse.QuestionResponseVideos || [])
      .some((video) => isValidUrl(video.videoUrl));
  }

  if (AUDIO_QUESTION_TYPES.includes(type)
    || [
      'long answer',
      'formatted answer',
      'numeric answer',
      'short answer',
      'short-text',
      'date',
      'price',
      'decimal answer',
      'barcode question',
      'time',
      'nps question',
      'text',
      'satisfaction',
    ].includes(type)) {
    return !questionResponse.value;
  }

  if (type === 'drop down menu' || type === 'yes/no question') {
    return !selectedQuestionChoice;
  }

  if (type === 'simple selection') {
    const otherOption = questionChoices.find((questionChoice) => questionChoice.type === 'other');
    return !selectedQuestionChoice
      && !(otherOption != null && questionResponse.value != null);
  }

  if (type === 'multiple selection') {
    const otherOption = questionChoices.find((questionChoice) => questionChoice.type === 'other');
    const hasSelectedNonOtherChoice = choiceResponses.some((choiceResponse) => {
      const choice = questionChoices.find(
        (questionChoice) => questionChoice.id === choiceResponse.questionChoiceId,
      );
      return choice && choice.id !== otherOption?.id;
    });
    return !hasSelectedNonOtherChoice
      && !(otherOption != null && questionResponse.value != null);
  }

  if (type === 'signature question' || type === 'user selection') {
    return !questionResponse.value;
  }

  return true;
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const QuestionResponses = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { taskResponseId } = useParams();

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showUnanswered, setShowUnanswered] = useState(true);
  const debouncedSearchText = useDebounce(searchText, 400);
  const [surveyResponse, setSurveyResponse] = useState({});
  const [selectedQuestionResponseList, setSelectedQuestionResponseList] = useState([]);
  const [selectedQuestionResponseImageList, setSelectedQuestionResponseImageList] = useState([]);
  const historyReactRouter = useHistory();
  const [showEmptySelectedQuestionResponseListSnack,
    setShowEmptySelectedQuestionResponseListSnack] = useState(false);

  const rowsPerPage = 10;
  const [questionResponsesQuantity, setQuestionResponsesQuantity] = useState(0);
  const [allQuestionResponses, setAllQuestionResponses] = useState([]);
  const [filteredQuestionResponses, setFilteredQuestionResponses] = useState([]);
  const [questionResponses, setQuestionResponses] = useState([]);
  const [imageQuestionResponses, setImageQuestionResponses] = useState([]);
  const [videoQuestionResponses, setVideoQuestionResponses] = useState([]);
  const [audioQuestionResponses, setAudioQuestionResponses] = useState([]);
  const account = useSelector((state) => state.account);
  const [page, setPage] = useState(0);

  /**
   * Set survey response review as Pending state.
   *
   * @param {int} surveyResponseId Survey response identifier
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const markStartSurveyResponseReview = async (surveyResponseId) => {
    const a = await startSurveyResponseReview(surveyResponseId);

    if (a.status === OK.status) {
      // no op
      // console.log('Starting Task response review');
    } else if (
      a.status === CONFLICT.status
      && parseInt(account.user.id, 10) !== a.data.surveyResponseReview.reviewedBy) {
      // Already started
      //      alert(a.data.errorMessage);
    }
  };

  const taskResponseIdRef = useRef(taskResponseId);
  taskResponseIdRef.current = taskResponseId;
  const markStartSurveyResponseReviewRef = useRef(markStartSurveyResponseReview);
  markStartSurveyResponseReviewRef.current = markStartSurveyResponseReview;

  useEffect(() => {
    markStartSurveyResponseReviewRef.current(taskResponseIdRef.current);
  }, []);

  /**
   * Load survey response from backend.
   */
  useEffect(() => {
    setLoading(true);
    async function getSurveyResponseData() {
      try {
        const surveyResponseData = await getSurveyResponseById(taskResponseId);
        if (surveyResponseData.success) {
          let filteredQuestions = surveyResponseData.data.data.QuestionResponses.filter(
            (qr) => {
              const lowerCaseType = qr.Question.type.toLowerCase();
              return (
                !lowerCaseType.includes('page')
                && !lowerCaseType.includes('content')
              );
            },
          );

          filteredQuestions = filteredQuestions.sort((a, b) => {
            if (a.productId === b.productId) {
              return a.Question.order - b.Question.order;
            }
            return a.productId - b.productId;
          });

          const imageQuestions = surveyResponseData.data.data.QuestionResponses.filter(
            (qr) => qr.Question.type.toLowerCase().includes('image'),
          );

          const videoQuestions = surveyResponseData.data.data.QuestionResponses.filter(
            (qr) => qr.Question.type.toLowerCase() === 'video',
          );

          const audioQuestions = surveyResponseData.data.data.QuestionResponses.filter(
            (qr) => AUDIO_QUESTION_TYPES.includes(qr.Question.type.toLowerCase())
              && isValidUrl(qr.value),
          );

          setSurveyResponse(surveyResponseData.data.data);
          setAllQuestionResponses(filteredQuestions);
          setFilteredQuestionResponses(filteredQuestions);
          setQuestionResponses(filteredQuestions.slice(
            page * rowsPerPage, rowsPerPage * (page + 1),
          ));
          setImageQuestionResponses(imageQuestions);
          setVideoQuestionResponses(videoQuestions);
          setAudioQuestionResponses(audioQuestions);
          setQuestionResponsesQuantity(filteredQuestions.length);
        }
      } finally {
        setLoading(false);
      }
    }
    getSurveyResponseData();
    // solo necesario para el taskResponseId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskResponseId]);

  useEffect(() => {
    let filteredQuestions = allQuestionResponses;
    const normalizedSearchText = debouncedSearchText.trim().toLowerCase();

    if (normalizedSearchText !== '') {
      filteredQuestions = filteredQuestions.filter((qr) => {
        const questionTitle = qr.Question?.title || '';
        const productName = qr.Product?.name || '';
        return questionTitle.toLowerCase().includes(normalizedSearchText)
          || productName.toLowerCase().includes(normalizedSearchText);
      });
    }

    if (!showUnanswered) {
      filteredQuestions = filteredQuestions.filter(
        (qr) => !isQuestionResponseWithoutAnswer(qr),
      );
    }

    setQuestionResponsesQuantity(filteredQuestions.length);
    setFilteredQuestionResponses(filteredQuestions);
    setPage(0);
  }, [allQuestionResponses, debouncedSearchText, showUnanswered]);

  useEffect(() => {
    setQuestionResponses(filteredQuestionResponses.slice(
      page * rowsPerPage, rowsPerPage * (page + 1),
    ));
  }, [page, filteredQuestionResponses]);

  const handleChangePage = (newPage) => {
    setPage(Number.parseInt(newPage, 10));
  };

  /**
   * Keep a list of question responses that need to be rejected.
   *
   * @param {int} questionResponseId Question Response identifier
   * @returns {undefined}
   */
  const handleSelectedQuestionResponse = (questionResponseId) => {
    const indexOf = selectedQuestionResponseList.indexOf(questionResponseId);
    const tempSelectedQuestionResponseList = selectedQuestionResponseList;

    if (indexOf !== -1) {
      tempSelectedQuestionResponseList.splice(indexOf, 1);
    } else {
      tempSelectedQuestionResponseList.push(questionResponseId);
    }
    setSelectedQuestionResponseList(tempSelectedQuestionResponseList);
  };

  const tableHeaders = [
    {
      id: 'questionId',
      width: '7%',
      label: <b>{t('qa.questionResponseView.tableHeaders.questionId')}</b>,
    },
    {
      id: 'id',
      width: '7%',
      label: <b>{t('qa.questionResponseView.tableHeaders.id')}</b>,
    },
    {
      id: 'title',
      width: '17%',
      label: <b>{t('qa.questionResponseView.tableHeaders.title')}</b>,
    },
    {
      id: 'product',
      width: '12%',
      label: <b>{t('qa.questionResponseView.tableHeaders.product')}</b>,
    },
    {
      id: 'description',
      width: '12%',
      label: <b>{t('qa.questionResponseView.tableHeaders.description')}</b>,
    },
    {
      id: 'type',
      width: '7%',
      label: <b>{t('qa.questionResponseView.tableHeaders.type')}</b>,
    },
    {
      id: 'answer',
      width: '30%',
      label: <b>{t('qa.questionResponseView.tableHeaders.answer')}</b>,
    },
    {
      id: 'edit',
      width: '8%',
      label: <b>{t('qa.questionResponseView.tableHeaders.edit')}</b>,
    },
  ];

  const handleType = (type) => {
    switch (type) {
      case 'content':
        return (
          <b>{t('qa.questionResponseView.questionTypes.content')}</b>
        );
      case 'image':
        return (
          <b>{t('qa.questionResponseView.questionTypes.image')}</b>
        );
      case 'end page':
        return (
          <b>{t('qa.questionResponseView.questionTypes.endPage')}</b>
        );
      case 'long answer':
        return (
          <b>{t('qa.questionResponseView.questionTypes.longAnswer')}</b>
        );
      case 'numeric answer':
        return (
          <b>{t('qa.questionResponseView.questionTypes.numericAnswer')}</b>
        );
      case 'nps question':
        return (
          <b>{t('qa.questionResponseView.questionTypes.npsQuestion')}</b>
        );
      case 'drop down menu':
        return (
          <b>{t('qa.questionResponseView.questionTypes.dropDownMenu')}</b>
        );
      case 'simple selection':
        return (
          <b>{t('qa.questionResponseView.questionTypes.simpleSelection')}</b>
        );
      case 'satisfaction':
        return (
          <b>{t('qa.questionResponseView.questionTypes.satisfaction')}</b>
        );
      case 'short answer':
        return (
          <b>{t('qa.questionResponseView.questionTypes.shortAnswer')}</b>
        );
      case 'short-text':
        return (
          <b>{t('qa.questionResponseView.questionTypes.shortText')}</b>
        );
      case 'signature question':
        return (
          <b>{t('qa.questionResponseView.questionTypes.signature')}</b>
        );
      case 'text':
        return (
          <b>{t('qa.questionResponseView.questionTypes.text')}</b>
        );
      case 'multiple selection':
        return (
          <b>{t('qa.questionResponseView.questionTypes.multipleSelection')}</b>
        );
      case 'yes/no question':
        return (
          <b>{t('qa.questionResponseView.questionTypes.yesNoQuestion')}</b>
        );
      case 'welcome page':
        return (
          <b>{t('qa.questionResponseView.questionTypes.welcomePage')}</b>
        );
      case 'multiple image question':
        return (
          <b>{t('qa.questionResponseView.questionTypes.multipleImageQuestion')}</b>
        );
      case 'date':
        return (
          <b>{t('qa.questionResponseView.questionTypes.date')}</b>
        );
      case 'barcode question':
        return (
          <b>{t('qa.questionResponseView.questionTypes.barcode')}</b>
        );
      case 'decimal answer':
        return (
          <b>{t('qa.questionResponseView.questionTypes.decimal')}</b>
        );
      case 'price':
        return (
          <b>{t('qa.questionResponseView.questionTypes.price')}</b>
        );
      case 'audio':
        return (
          <b>{t('qa.questionResponseView.questionTypes.audio')}</b>
        );
      case 'audio-background':
        return (
          <b>{t('qa.questionResponseView.questionTypes.audioBackground')}</b>
        );
      case 'time':
        return (
          <b>{t('qa.questionResponseView.questionTypes.time')}</b>
        );
      case 'video':
        return (
          <b>{t('qa.questionResponseView.questionTypes.video')}</b>
        );
      case 'formatted answer':
        return (
          <b>{t('qa.questionResponseView.questionTypes.formatted')}</b>
        );
      case 'user selection':
        return (
          <b>{t('qa.questionResponseView.questionTypes.userSelection')}</b>
        );
      default:
        return (
          <b>{t('qa.questionResponseView.questionTypes.default')}</b>
        );
    }
  };

  const handleQuestionResponseValue = (questionResponse) => {
    let htmlResponse = '';

    switch (questionResponse.Question.type) {
      case 'audio':
      case 'audio-background':
        htmlResponse = questionResponse.value ? questionResponse.value : t('qa.questionResponseView.questionTypes.noAnswer');
        break;
      case 'long answer':
      case 'formatted answer':
      case 'numeric answer':
      case 'short answer':
      case 'short-text':
      case 'date':
      case 'price':
      case 'decimal answer':
      case 'barcode question':
      case 'time':
      case 'nps question':
        htmlResponse = questionResponse.value ? questionResponse.value : t('qa.questionResponseView.questionTypes.noAnswer');
        break;
      case 'text':
        htmlResponse = questionResponse.value ? questionResponse.value : t('qa.questionResponseView.questionTypes.noAnswer');
        break;
      case 'drop down menu':
        try {
          const selectedQuestionChoiceId = questionResponse
            .QuestionChoiceResponses[0].questionChoiceId;
          const selectedQuestionChoice = questionResponse
            .Question.QuestionChoices.filter(
              (questionChoice) => questionChoice.id === selectedQuestionChoiceId,
            )[0];
          htmlResponse = selectedQuestionChoice.title;
        } catch (e) {
          htmlResponse = t('qa.questionResponseView.questionTypes.noAnswer');
        }
        break;
      case 'simple selection':
        try {
          const selectedQuestionChoiceId = questionResponse
            .QuestionChoiceResponses[0].questionChoiceId;
          const selectedQuestionChoice = questionResponse
            .Question.QuestionChoices.filter(
              (questionChoice) => questionChoice.id === selectedQuestionChoiceId,
            )[0];

          const otherOption = questionResponse
            .Question.QuestionChoices.filter(
              (questionChoice) => questionChoice.type === 'other',
            )[0];

          if (otherOption != null && questionResponse.value != null) {
            htmlResponse = questionResponse.value;
          } else {
            htmlResponse = selectedQuestionChoice.title;
          }
        } catch (e) {
          htmlResponse = t('qa.questionResponseView.questionTypes.noAnswer');
        }
        break;
      case 'satisfaction':
        htmlResponse = questionResponse.value ? questionResponse.value : t('qa.questionResponseView.questionTypes.noAnswer');
        break;
      case 'signature question':
        htmlResponse = questionResponse.value ? <SignatureCanvas data={questionResponse.value} /> : 'Firma';
        break;
      case 'user selection':
        if (!questionResponse.value) {
          htmlResponse = t('qa.questionResponseView.questionTypes.noAnswer');
        } else {
          htmlResponse = questionResponse.SelectedUser
            ? questionResponse.SelectedUser.email
            : t('qa.questionResponseView.questionTypes.userNotFound');
        }
        break;
      case 'multiple selection':
        try {
          let otherOption = null;
          const arrayText = [];
          questionResponse.QuestionChoiceResponses.forEach((questionChoiceResponse) => {
            const selectedQuestionChoiceId = questionChoiceResponse.questionChoiceId;
            const selectedQuestionChoice = questionResponse
              .Question.QuestionChoices.filter(
                (questionChoice) => questionChoice.id === selectedQuestionChoiceId,
              )[0];

            [otherOption] = questionResponse
              .Question.QuestionChoices.filter(
                (questionChoice) => questionChoice.type === 'other',
              );

            if (selectedQuestionChoice.id !== otherOption?.id) {
              arrayText.push(selectedQuestionChoice.title);
            }
          });

          if (otherOption != null && questionResponse.value != null) {
            arrayText.push(questionResponse.value);
          }
          htmlResponse = arrayText.length > 0
            ? arrayText.join(', ')
            : t('qa.questionResponseView.questionTypes.noAnswer');
        } catch (e) {
          htmlResponse = t('qa.questionResponseView.questionTypes.noAnswer');
        }
        break;
      case 'yes/no question':
        try {
          const selectedQuestionChoiceId = questionResponse
            .QuestionChoiceResponses[0].questionChoiceId;
          const selectedQuestionChoice = questionResponse
            .Question.QuestionChoices.filter(
              (questionChoice) => questionChoice.id === selectedQuestionChoiceId,
            )[0];
          htmlResponse = selectedQuestionChoice.title;
        } catch (e) {
          htmlResponse = t('qa.questionResponseView.questionTypes.noAnswer');
        }
        break;
      case 'content':
      case 'image':
        return '';
      case 'multiple image question':
        return '';
      case 'video':
        return '';
      case 'end page':
      case 'welcome page':
      default: htmlResponse = t('qa.questionResponseView.questionTypes.noAnswer');
    }
    return htmlResponse;
  };

  /* Build questions responses list */
  const questionsTableRows = () => {
    if (questionResponses.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} className={styles.emptyState}>
            <h4>{t('qa.noTaskResponses')}</h4>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        {questionResponses
          .map((row) => (
            <TableRow
              hover
              key={row.id}
            >{<>
              <TableCell align="left" width="7%">{row.Question.id}</TableCell>
              <TableCell align="left" width="7%">{row.id}</TableCell>
              <TableCell
                component="th"
                scope="row"
                padding="none"
                className={styles.questionsTitle}
                width="17%"
              >{row.Question.title ? row.Question.title : t('qa.questionResponseView.noTitle')}</TableCell>
              <TableCell align="left" width="12%">{row.Product
                ? row.Product.name : t('qa.questionResponseView.noProduct')}</TableCell>
              <TableCell align="left" width="12%">{row.Question.description
                ? row.Question.description : t('qa.questionResponseView.noDescription')}</TableCell>
              <TableCell align="left" width="7%">{handleType(row.Question.type)}</TableCell>
              <TableCell align="left" width="30%">
                {(() => {
                  const type = row.Question.type.toLowerCase();

                  // Imagen / imágenes múltiples: enlaces enmascarados
                  if (type.includes('image')) {
                    const images = (row.QuestionResponseImages || [])
                      .filter((img) => isValidUrl(img.imageUrl));
                    if (images.length === 0) {
                      return t('qa.questionResponseView.questionTypes.noAnswer');
                    }
                    return (
                      <Box display="flex" flexDirection="column">
                        {images.map((img) => (
                          <a
                            key={img.id}
                            href={img.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.taskLink}
                          >
                            {`imagen_${img.id}`}
                          </a>
                        ))}
                      </Box>
                    );
                  }

                  // Video: enlaces enmascarados
                  if (type === 'video') {
                    const videos = (row.QuestionResponseVideos || [])
                      .filter((vid) => isValidUrl(vid.videoUrl));
                    if (videos.length === 0) {
                      return t('qa.questionResponseView.questionTypes.noAnswer');
                    }
                    return (
                      <Box display="flex" flexDirection="column">
                        {videos.map((vid) => (
                          <a
                            key={vid.id}
                            href={vid.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.taskLink}
                          >
                            {`video_${vid.id}`}
                          </a>
                        ))}
                      </Box>
                    );
                  }

                  const value = handleQuestionResponseValue(row);

                  // Audio: enlace enmascarado
                  if (AUDIO_QUESTION_TYPES.includes(type) && isValidUrl(value)) {
                    return (
                      <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.taskLink}
                      >
                        {`audio_${row.id}`}
                      </a>
                    );
                  }

                  return isValidUrl(value) ? (
                    <a
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.taskLink}
                    >{value}</a>
                  ) : (
                    value
                  );
                })()}
              </TableCell>
              <TableCell align="center" width="8%">
                {row.Question.type !== 'user selection' && (
                  <IconButton component={Link} to={`${taskResponseId}/${row.id}/edit`}>
                    <EditIcon titleAccess="Editar" />
                  </IconButton>
                )}
              </TableCell>
            </>
              }
            </TableRow >
          ))}
      </>
    );
  };

  const questionSearchBar = () => (
    <Grid container className={styles.searchBar}>
      <Grid item xs={1}>
        <Typography variant="h1">
          {t('responses.searchBarLabel')}
        </Typography>
      </Grid>
      <Grid item xs={11}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <TextField
            id="search"
            label={t('responses.searchBarQuestion')}
            variant="outlined"
            size="small"
            fullWidth
            style={{ flex: '0 0 50%', marginLeft: '-8px', marginRight: '16px' }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <FormControlLabel
            control={(
              <Switch
                checked={showUnanswered}
                onChange={(e) => setShowUnanswered(e.target.checked)}
                color="primary"
              />
            )}
            label={t('responses.showUnanswered')}
          />
        </Box>
      </Grid>
    </Grid >
  );

  return (
    <div className={styles.content}>
      <IconButton
        id="backButton"
        aria-label="back"
        className={styles.backButtonQuestionResponse}
        onClick={() => historyReactRouter.push('/responses')}
      >
        <BackIcon fontSize="small" />
        <span>{t('groups.actionButtons.back')}</span>
      </IconButton>
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '60vh', width: '100%' }}
        >
          <CircularProgress />
          <h4>{t('loading')}</h4>
        </Box>
      ) : (
      <>
      <DetailResponse responseDetail={{
        surveyResponseId: surveyResponse?.id,
        locationId: surveyResponse?.TaskInstance?.Location?.id,
        locationName: surveyResponse?.TaskInstance?.Location?.name,
        taskId: surveyResponse?.Survey?.id,
        taskName: surveyResponse?.Survey?.title,
        type: surveyResponse?.Survey?.type,
        category: surveyResponse?.categoryCode,
        userName: surveyResponse?.TaskInstance?.User?.fullName,
        userEmail: surveyResponse?.TaskInstance?.User?.email,
        startedAt: surveyResponse?.startedAt,
        finishedAt: surveyResponse?.finishedAt,
        label: surveyResponse?.Survey?.label,
        createdAt: surveyResponse?.createdAt,
        startedAtReal: surveyResponse?.startedAtReal,
        deleted: surveyResponse?.deleted,
        reviewStatus: surveyResponse?.SurveyResponseReview?.status,
      }}
      ></DetailResponse>
      <br />
      {/* Header with title */}
      <div className={styles.header}>
        <Box sx={{ width: '100%' }}>
          <Typography variant='h1' className={styles.title} gutterBottom>
            {t('qa.questionResponseView.title')}
          </Typography>
          <Typography variant='h4' gutterBottom display="inline">
            {surveyResponse.Survey ? surveyResponse.Survey.title : ''}
          </Typography>
        </Box>
      </div>

      {/* Content */}
      <Paper className={styles.documentsTable}>
        {/* Carrousel for image responses */}
        <Grid container direction='row' alignItems='center' spacing={3}>
          <Grid item xs={12}>
            <Card className={styles.carrousel} style={imageQuestionResponses.length === 0 ? { minHeight: '120px' } : {}}>
              <CardContent>
                <>
                  <Grid alignItems='center' container>
                    <Grid item xs md={9} xl={10}>
                      <Typography variant='h2'>
                        {t('qa.questionResponseView.imageTitle')}
                      </Typography>
                    </Grid>
                  </Grid>
                  {
                    imageQuestionResponses.length > 0
                      ? <SurveyImageCarousel
                        surveyResponseId={taskResponseId}
                        questionResponseWithImageList={imageQuestionResponses}
                        selectedQuestionResponseImageList={selectedQuestionResponseImageList}
                        setSelectedQuestionResponseImageList={setSelectedQuestionResponseImageList}
                      />
                      : <Typography>{t('qa.questionResponseView.noImages')}</Typography>
                  }
                </>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Carrousel for video responses */}
        <Grid container direction='row' alignItems='center' spacing={3}>
          <Grid item xs={12}>
            <Card className={styles.carrousel} style={videoQuestionResponses.length === 0 ? { minHeight: '120px' } : {}}>
              <CardContent>
                <>
                  <Grid alignItems='center' container>
                    <Grid item xs md={9} xl={10}>
                      <Typography variant='h2'>
                        {t('qa.questionResponseView.videoTitle')}
                      </Typography>
                    </Grid>
                  </Grid>
                  {
                    videoQuestionResponses.length > 0
                      ? <SurveyVideoCarousel
                        surveyResponseId={taskResponseId}
                        questionResponseWithVideoList={videoQuestionResponses}
                        handleSelectedQuestionResponse={handleSelectedQuestionResponse}
                      />
                      : <Typography>{t('qa.questionResponseView.noVideos')}</Typography>
                  }
                </>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Audio responses */}
        <Grid container direction='row' alignItems='center' spacing={3}>
          <Grid item xs={12}>
            <Card className={styles.audioContainer} style={audioQuestionResponses.length === 0 ? { minHeight: '120px' } : {}}>
              <CardContent>
                <>
                  <Grid alignItems='center' container>
                    <Grid item xs md={9} xl={10}>
                      <Typography variant='h2'>
                        {t('qa.questionResponseView.audioTitle')}
                      </Typography>
                    </Grid>
                  </Grid>
                  {
                    audioQuestionResponses.length > 0
                      ? (
                        <Grid container direction='row' spacing={3} style={{ marginTop: '8px' }}>
                          {audioQuestionResponses.map((audioResponse) => (
                            <Grid item xs={12} md={6} key={audioResponse.id}>
                              <Card className={styles.card}>
                                <CardContent>
                                  <Box display="flex" flexDirection="column">
                                    <Typography variant='h4' gutterBottom>
                                      {audioResponse.Question.title
                                        ? audioResponse.Question.title
                                        : t('qa.questionResponseView.noTitle')}
                                    </Typography>
                                    <audio controls src={audioResponse.value} style={{ width: '100%' }}>
                                      {t('qa.questionResponseView.noAudios')}
                                    </audio>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )
                      : <Typography>{t('qa.questionResponseView.noAudios')}</Typography>
                  }
                </>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      <br />
      {/* Questions responses */}
      <Paper className={styles.questionListTable}>
        {questionSearchBar()}
        <Table
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              {tableHeaders.map((tableHeader) => (
                <TableCell
                  key={tableHeader.id}
                  className={clsx({
                    [styles.leftHeader]: true,
                    [styles.centerHeader]: ['check', 'edit'].includes(tableHeader.id),
                  })}
                  padding="normal"
                  align="left"
                  style={{ width: tableHeader.width }}
                >
                  {tableHeader.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className={styles.progress}>
                  <CircularProgress />
                  <h4>{t('qa.loading')}</h4>
                </TableCell>
              </TableRow>
            ) : questionsTableRows()}
          </TableBody>
        </Table>

        { /* Pagination stripe */}
        <Pagination
          setPage={setPage}
          handleChangePage={handleChangePage}
          page={page}
          rowsPerPage={rowsPerPage}
          quantity={questionResponsesQuantity}
        />
      </Paper>
      { /* Snack message for empty selected question list when rejecting
        selected questions */ }
      <Snackbar
        open={showEmptySelectedQuestionResponseListSnack}
        message={t('qa.selectAtLeastOneAnswer')}
        severity="error"
        onClose={() => setShowEmptySelectedQuestionResponseListSnack(false)}
      />
      </>
      )}
    </div >
  );
};

export default QuestionResponses;
